import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/Auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import BulkDrugUploadModal from "../BulkDrugUploadModal";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Upload,
  Edit2,
  Trash2,
} from "lucide-react";
import DebouncedSearchInput from "../../../components/common/DebouncedSearchInput";
import RoleBasedComponent from "../../../components/RoleBasedComponent";
import ExportData from "../../../components/common/ExportData";
import SortableTableHeader from "../../../components/Table/SortableTableHeader";
import { BiSync } from "react-icons/bi";

const CompanyInventory = () => {
  const { requestWithAuth } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [allInventory, setAllInventory] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchInventory = async () => {
    setLoading(true);

    try {
      const sortParam = sortBy
        ? `&sort_by=${sortBy}&sort_order=${sortOrder}`
        : "";
      const data = await requestWithAuth(
        `/inventory/company/?paginated=false${sortParam}`,
        "GET"
      );
      setAllInventory(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Error fetching inventory data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async () => {
    setLoading(true);
    try {
      const sortParam = sortBy
        ? `&sort_by=${sortBy}&sort_order=${sortOrder}`
        : "";
      const data = await requestWithAuth(
        `/inventory/company/?paginated=false${sortParam}`,
        "GET"
      );
      setTableData(data);
    } catch (error) {
      console.error("Error fetching inventory data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterInventoryList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;
    const totalPagesToShow = 5;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      const allIds = inventory.map((item) => item.id);
      setSelectedItems(allIds);
      setSelectAll(true);
    }
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      const newSelectedItems = selectedItems.filter((id) => id !== itemId);
      setSelectedItems(newSelectedItems);
      setSelectAll(false);
    } else {
      const newSelectedItems = [...selectedItems, itemId];
      setSelectedItems(newSelectedItems);

      if (newSelectedItems.length === inventory.length) {
        setSelectAll(true);
      }
    }
  };

  const deleteItem = (drug_id) => {
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to delete this item?",
      showDenyButton: true,
      confirmButtonText: "Yes Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeleting(true);
        try {
          const data = await requestWithAuth(
            `/inventory/company/${drug_id}/`,
            "DELETE"
          );
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Drug Deleted Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something unexpected happened.");
          console.error("Error deleting drug:", error);
        } finally {
          setDeleting(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const syncItem = async (drug_id) => {
    Swal.fire({
      icon: "question",
      title: "Sync Drug to All Branches?",
      text: "This will add the drug to any branch inventory where it is missing.",
      showDenyButton: true,
      confirmButtonText: "Yes, Sync!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSyncing(true);
        try {
          await requestWithAuth(
            `/inventory/drug/${drug_id}/sync/`,
            "PATCH",
            {}
          );
          Swal.fire({
            icon: "success",
            title: "Synced!",
            text: "Drug successfully synced to all branches.",
          });
        } catch (error) {
          toast.error("Failed to sync drug. Please try again.");
          console.error("Error syncing drug:", error);
        } finally {
          setSyncing(false);
        }
      }
    });
  };

  const bulkDeleteItems = () => {
    if (selectedItems.length === 0) {
      toast.error("Please select items to delete");
      return;
    }

    Swal.fire({
      icon: "question",
      title: `Are you sure you want to delete ${selectedItems.length} selected item(s)?`,
      showDenyButton: true,
      confirmButtonText: "Yes Delete All!",
      denyButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setBulkDeleting(true);
        try {
          const deletePromises = selectedItems.map((itemId) =>
            requestWithAuth(`/inventory/company/${itemId}/`, "DELETE")
          );

          await Promise.all(deletePromises);

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: `${selectedItems.length} item(s) deleted successfully`,
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something unexpected happened during bulk deletion.");
        } finally {
          setBulkDeleting(false);
        }
      }
    });
  };

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [inventory]);

  useEffect(() => {
    fetchInventory();
    getTableData();
  }, [sortBy, sortOrder]);

  useEffect(() => {
    let filtered = allInventory;
    if (search) {
      filtered = allInventory.filter(
        (item) =>
          item?.name?.toLowerCase().includes(search.toLowerCase()) ||
          item?.id?.toString().includes(search.toLowerCase())
      );
    }

    const count = filtered.length;
    setTotalPages(Math.ceil(count / pageSize));

    const startIndex = (currentPage - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + Number(pageSize));

    setInventory(paginated);
    setNextPage(currentPage < Math.ceil(count / pageSize));
    setPrevPage(currentPage > 1);
  }, [allInventory, search, currentPage, pageSize]);

  useEffect(() => {
    getTableData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Drugs</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Inventory</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Company Inventory
              </h1>
            </div>
            <RoleBasedComponent
              allowedRoles={["superadmin", "admin", "stores"]}
              renderComponent={() => (
                <div className="flex gap-3">
                  <Link
                    to="/administration/inventory/create"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
                  >
                    <Plus size={20} />
                    Create Drug
                  </Link>
                  <button
                    onClick={() => setIsModalOpen(!isModalOpen)}
                    className="bg-white hover:bg-gray-50 text-blue-600 border border-blue-600 px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
                  >
                    <Upload size={20} />
                    Bulk Upload
                  </button>
                </div>
              )}
            />
          </div>
        </div>

        <BulkDrugUploadModal
          isOpen={isModalOpen}
          onClose={() => window.location.reload()}
          onUploadSuccess={fetchInventory}
        />

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName="Company Inventory"
                />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>

                {selectedItems.length > 0 && (
                  <button
                    onClick={bulkDeleteItems}
                    disabled={bulkDeleting}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 ml-2"
                  >
                    {bulkDeleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        Delete Selected ({selectedItems.length})
                      </>
                    )}
                  </button>
                )}
              </div>

              <DebouncedSearchInput
                onSearch={filterInventoryList}
                placeholder="Search inventory..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading inventory...</span>
                </div>
              </div>
            ) : inventory?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <RoleBasedComponent
                      allowedRoles={["superadmin", "admin"]}
                      renderComponent={() => (
                        <th className="text-left py-4 px-6">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                      )}
                    />
                    <SortableTableHeader
                      label="ID"
                      fieldKey="id"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Description"
                      fieldKey="name"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <RoleBasedComponent
                      allowedRoles={[
                        "superadmin",
                        "admin",
                        "warehouse_manager",
                        "stores",
                      ]}
                      renderComponent={() => (
                        <SortableTableHeader
                          label="Cost Price"
                          fieldKey="cost_price"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                      )}
                    />
                    <SortableTableHeader
                      label="Selling Price"
                      fieldKey="cash_price"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <RoleBasedComponent
                      allowedRoles={[
                        "superadmin",
                        "admin",
                        "warehouse_manager",
                        "stores",
                      ]}
                      renderComponent={() => (
                        <SortableTableHeader
                          label="Reorder Level"
                          fieldKey="reorder_level"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                      )}
                    />

                    <RoleBasedComponent
                      allowedRoles={[
                        "superadmin",
                        "admin",
                        "warehouse_manager",
                        "stores",
                      ]}
                      renderComponent={() => (
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inventory?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <RoleBasedComponent
                        allowedRoles={["superadmin", "admin"]}
                        renderComponent={() => (
                          <td className="py-4 px-6">
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(item?.id)}
                              onChange={() => handleSelectItem(item?.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        )}
                      />
                      <td className="py-4 px-6 text-sm text-gray-900">
                        {item?.id}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.name}
                      </td>
                      <RoleBasedComponent
                        allowedRoles={[
                          "superadmin",
                          "admin",
                          "warehouse_manager",
                          "stores",
                        ]}
                        renderComponent={() => (
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.cost_price}
                          </td>
                        )}
                      />
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.cash_price}
                      </td>
                      <RoleBasedComponent
                        allowedRoles={[
                          "superadmin",
                          "admin",
                          "warehouse_manager",
                          "stores",
                        ]}
                        renderComponent={() => (
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.reorder_level}
                          </td>
                        )}
                      />

                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <RoleBasedComponent
                            allowedRoles={[
                              "superadmin",
                              "admin",
                              "warehouse_manager",
                              "stores",
                            ]}
                          >
                            <Link
                              to={`/administration/inventory/${item?.id}/modify/`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Edit2 size={14} />
                              Edit
                            </Link>
                          </RoleBasedComponent>
                          <RoleBasedComponent
                            allowedRoles={["superadmin", "admin", "stores"]}
                          >
                            <button
                              onClick={() => syncItem(item?.id)}
                              disabled={syncing}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                            >
                              {syncing ? (
                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-green-700"></div>
                              ) : (
                                <BiSync size={14} />
                              )}
                              Sync
                            </button>
                          </RoleBasedComponent>
                          <RoleBasedComponent
                            allowedRoles={["superadmin", "admin"]}
                          >
                            <button
                              onClick={() => deleteItem(item?.id)}
                              disabled={deleting}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              {deleting ? (
                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-red-700"></div>
                              ) : (
                                <Trash2 size={14} />
                              )}
                              Delete
                            </button>
                          </RoleBasedComponent>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">
                  No inventory items found
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {inventory?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyInventory;
